import { useState, useEffect, useCallback } from 'react';
import { useAsyncOperation } from './useErrorHandler';
import { useRealtimeRefresh } from './useRealtimeUpdates';
import { useSeason } from './useSeason';
import { ERROR_CODES, AppErrorClass } from '../utils/errorHandling';
import { fetchPlayerStatsOptimized } from '../utils/optimizedDataFetching';
import type { UsePlayerStatsReturn, PlayerStats } from '../types';

export function usePlayerStats(bowlerId: number | null, seasonYearOverride?: number): UsePlayerStatsReturn {
  const { selectedSeason } = useSeason();
  const seasonYear = seasonYearOverride ?? selectedSeason;
  const [stats, setStats] = useState<PlayerStats | null>(null);
  
  const {
    loading,
    error,
    execute,
    refetch: retryOperation,
    clearError,
  } = useAsyncOperation<PlayerStats>({
    onSuccess: (data) => setStats(data),
    onError: (error) => {
      console.error('Error fetching player stats:', error);
    },
  });

  const fetchPlayerStats = useCallback(async (): Promise<PlayerStats> => {
    if (!bowlerId) {
      throw new AppErrorClass(
        ERROR_CODES.VALIDATION_REQUIRED_FIELD,
        'Player ID is required to fetch statistics'
      );
    }

    return await fetchPlayerStatsOptimized(bowlerId, seasonYear);
  }, [bowlerId, seasonYear]);

  const refetch = async () => {
    if (bowlerId) {
      await execute(fetchPlayerStats, {
        context: { 
          operation: 'fetch_player_stats',
          bowlerId,
          seasonYear,
        },
      });
    } else {
      setStats(null);
      clearError();
    }
  };

  useEffect(() => {
    if (bowlerId) {
      execute(fetchPlayerStats, {
        context: { 
          operation: 'initial_fetch_player_stats',
          bowlerId,
          seasonYear,
        },
      });
    } else {
      setStats(null);
      clearError();
    }
  }, [bowlerId, seasonYear]);

  // Set up real-time refresh
  useRealtimeRefresh(refetch, [bowlerId, seasonYear]);

  return {
    stats,
    loading,
    error,
    refetch,
    clearError,
    retryLastOperation: retryOperation,
  };
}